import { API } from '../config';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Lock, AlertTriangle, ShieldOff } from 'lucide-react';


export function PinGate({ children }) {
  const { token, getAuthHeaders, logout } = useAuth();
  const [pinStatus, setPinStatus] = useState(null);
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef([]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/security/pin/status`, { headers: getAuthHeaders() });
      setPinStatus(res.data);
      if (!res.data.pin_is_set) setVerified(true); // No PIN set, skip gate
      else {
        // Check session storage for verified state
        const v = sessionStorage.getItem('pin_verified');
        if (v === 'true') setVerified(true);
      }
    } catch { setVerified(true); } // If endpoint fails, don't block
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchStatus(); else setLoading(false); }, [token]); // eslint-disable-line

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown(p => { if (p <= 1) { clearInterval(t); return 0; } return p - 1; }), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  // Check lock_until
  useEffect(() => {
    if (pinStatus?.pin_lock_until) {
      const lockTime = new Date(pinStatus.pin_lock_until);
      const remaining = Math.max(0, Math.ceil((lockTime - Date.now()) / 1000));
      if (remaining > 0) setCountdown(remaining);
    }
  }, [pinStatus]);

  const handleInput = (idx, value) => {
    if (!/^\d?$/.test(value)) return;
    const newPin = [...pin];
    newPin[idx] = value;
    setPin(newPin);
    setError('');

    if (value && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }

    // Auto-submit when 6 digits entered
    if (value && idx === 5) {
      const fullPin = newPin.join('');
      if (fullPin.length === 6) {
        submitPin(fullPin);
      }
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !pin[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setPin(pasted.split(''));
      setTimeout(() => submitPin(pasted), 100);
    }
  };

  const submitPin = async (pinStr) => {
    setVerifying(true);
    setError('');
    try {
      const res = await axios.post(`${API}/security/pin/verify`, { pin: pinStr }, { headers: getAuthHeaders() });
      if (res.data.verified) {
        setVerified(true);
        sessionStorage.setItem('pin_verified', 'true');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'PIN doğrulanamadı';
      setError(detail);
      setPin(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();

      // Check if there's a time lock
      if (err.response?.status === 429) {
        const match = detail.match(/(\d+)\s*dakika\s*(\d+)?\s*saniye/);
        if (match) {
          setCountdown(parseInt(match[1]) * 60 + (parseInt(match[2]) || 0));
        } else {
          setCountdown(300); // Default 5 min
        }
      }

      // Refresh status
      try {
        const s = await axios.get(`${API}/security/pin/status`, { headers: getAuthHeaders() });
        setPinStatus(s.data);
      } catch {}
    } finally { setVerifying(false); }
  };

  if (loading) return null;
  if (verified) return children;

  const isTimeLocked = countdown > 0;
  const isFullyLocked = pinStatus?.pin_lock_level >= 3;
  const fmtCountdown = `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}`;

  return (
    <div className="min-h-dvh bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center space-y-6">
        <div className="flex justify-center">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${isFullyLocked ? 'bg-destructive/20' : 'bg-secondary'}`}>
            {isFullyLocked ? <ShieldOff className="w-8 h-8 text-destructive" /> : <Lock className="w-8 h-8 text-muted-foreground" />}
          </div>
        </div>

        {isFullyLocked ? (
          <>
            <div>
              <h1 className="text-lg font-semibold font-display text-destructive">Hesap Kilitlendi</h1>
              <p className="text-sm text-muted-foreground mt-1">Çok fazla yanlış deneme. Yöneticiyle iletişime geçin.</p>
            </div>
            <Button variant="outline" className="rounded-xl" onClick={logout}>Çıkış Yap</Button>
          </>
        ) : (
          <>
            <div>
              <h1 className="text-lg font-semibold font-display text-foreground">PIN Girin</h1>
              <p className="text-sm text-muted-foreground mt-1">Devam etmek için 6 haneli PIN'inizi girin</p>
            </div>

            {/* 6-digit PIN input */}
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {pin.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleInput(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  disabled={isTimeLocked || verifying}
                  className={`w-12 h-14 text-center text-xl font-bold rounded-xl border-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${
                    error ? 'border-destructive' : digit ? 'border-primary' : 'border-border'
                  } ${isTimeLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
              ))}
            </div>

            {/* Countdown */}
            {isTimeLocked && (
              <div className="bg-destructive/10 rounded-xl p-4 space-y-1">
                <AlertTriangle className="w-5 h-5 text-destructive mx-auto" />
                <p className="text-sm font-medium text-destructive">{fmtCountdown}</p>
                <p className="text-xs text-muted-foreground">Tekrar denemek için bekleyin</p>
              </div>
            )}

            {/* Error */}
            {error && !isTimeLocked && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            {/* Loading */}
            {verifying && <p className="text-sm text-muted-foreground">Doğrulanıyor...</p>}

            <button onClick={logout} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Çıkış yap</button>
          </>
        )}
      </div>
    </div>
  );
}
